import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const CreateTicketModal = ({ isOpen, onClose, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [description, setDescription] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');

  const [lineItems, setLineItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, price: '' });
  const [showAddItem, setShowAddItem] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
      // Reset form
      setDescription('');
      setServiceTypeId('');
      setPriorityId('');
      setCustomerId('');
      setStatusId('');
      setScheduledDate('');
      setScheduledTime('');
      setEstimatedDuration('');
      setLineItems([]);
      setNewItem({ name: '', quantity: 1, price: '' });
      setShowAddItem(false);
    }
  }, [isOpen]);

  const fetchDropdownData = async () => {
    try {
      const [stRes, prRes, cuRes, stRes2] = await Promise.all([
        supabase?.from('ticket_service_types')?.select('id, name')?.eq('is_active', true)?.order('name'),
        supabase?.from('ticket_priorities')?.select('id, name')?.eq('is_active', true)?.order('display_order'),
        supabase?.from('customers')?.select('id, name')?.order('name'),
        supabase?.from('ticket_statuses')?.select('id, name')?.eq('is_active', true)?.order('display_order'),
      ]);

      setServiceTypes(stRes?.data?.map(s => ({ value: s?.id, label: s?.name })) || []);
      setPriorities(prRes?.data?.map(p => ({ value: p?.id, label: p?.name })) || []);
      setCustomers(cuRes?.data?.map(c => ({ value: c?.id, label: c?.name })) || []);
      setStatuses(stRes2?.data?.map(s => ({ value: s?.id, label: s?.name })) || []);

      // Default to Pending status
      const pending = stRes2?.data?.find(s => s?.name?.toLowerCase() === 'pending');
      if (pending) setStatusId(pending?.id);
    } catch (err) {
      console.error('Error fetching dropdown data:', err?.message);
    }
  };

  const handleAddLineItem = () => {
    if (!newItem?.name?.trim()) return;
    setLineItems(prev => [
      ...prev,
      {
        id: Date.now(),
        name: newItem?.name?.trim(),
        quantity: parseInt(newItem?.quantity) || 1,
        price: parseFloat(newItem?.price) || 0,
      }
    ]);
    setNewItem({ name: '', quantity: 1, price: '' });
    setShowAddItem(false);
  };

  const handleRemoveLineItem = (id) => {
    setLineItems(prev => prev?.filter(item => item?.id !== id));
  };

  const handleCreateTicket = async () => {
    if (!customerId) {
      alert('Please select a customer.');
      return;
    }
    try {
      setSaving(true);

      const payload = {
        customer_id: customerId,
        description: description || null,
        estimated_duration: estimatedDuration || null,
      };

      if (serviceTypeId) payload.service_type_id = serviceTypeId;
      if (priorityId) payload.priority_id = priorityId;
      if (statusId) payload.status_id = statusId;

      if (scheduledDate) {
        payload.scheduled_date = scheduledTime
          ? `${scheduledDate}T${scheduledTime}`
          : `${scheduledDate}T00:00`;
      }

      const { data: newTicket, error: ticketError } = await supabase
        ?.from('tickets')
        ?.insert([payload])
        ?.select()
        ?.single();

      if (ticketError) throw ticketError;

      // Insert line items
      if (lineItems?.length > 0) {
        const products = lineItems?.map(item => ({
          ticket_id: newTicket?.id,
          name: item?.name,
          quantity: item?.quantity,
          price: item?.price,
        }));
        await supabase?.from('ticket_products')?.insert(products);
      }

      // Add history entry
      await supabase?.from('ticket_history')?.insert({
        ticket_id: newTicket?.id,
        action: 'Ticket Created',
        description: 'Ticket created manually without a quote.',
        user_name: 'System',
        icon: 'Plus'
      });

      onCreated?.();
      onClose();
    } catch (err) {
      console.error('Error creating ticket:', err?.message);
      alert(`Error: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const durationOptions = [
    { value: '', label: 'Not set' },
    { value: '1 hour', label: '1 hour' },
    { value: '2 hours', label: '2 hours' },
    { value: '3 hours', label: '3 hours' },
    { value: '4 hours', label: '4 hours' },
    { value: '6 hours', label: '6 hours' },
    { value: '8 hours', label: 'Full day (8 hours)' },
  ];

  const lineTotal = lineItems?.reduce((sum, item) => sum + (parseFloat(item?.price) * parseInt(item?.quantity || 1)), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">Create New Ticket</h2>
              <p className="text-xs text-muted-foreground">Create a ticket without a quote</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Ticket Details */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="FileText" size={15} color="var(--color-primary)" />
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Ticket Details</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Select
                  label="Customer *"
                  placeholder="Select customer"
                  options={customers}
                  value={customerId}
                  onChange={setCustomerId}
                />
              </div>
              <Select
                label="Service Type"
                placeholder="Select service type"
                options={serviceTypes}
                value={serviceTypeId}
                onChange={setServiceTypeId}
              />
              <Select
                label="Priority"
                placeholder="Select priority"
                options={priorities}
                value={priorityId}
                onChange={setPriorityId}
              />
              <Select
                label="Status"
                placeholder="Select status"
                options={statuses}
                value={statusId}
                onChange={setStatusId}
              />
              <Select
                label="Estimated Duration"
                placeholder="Select duration"
                options={durationOptions}
                value={estimatedDuration}
                onChange={setEstimatedDuration}
              />
              <Input
                label="Scheduled Date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e?.target?.value)}
              />
              <Input
                label="Scheduled Time"
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e?.target?.value)}
              />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e?.target?.value)}
                  placeholder="Describe the service work..."
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="Package" size={15} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Line Items</h3>
                <span className="text-xs text-muted-foreground">({lineItems?.length})</span>
              </div>
              {!showAddItem && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddItem(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Item
                </Button>
              )}
            </div>

            {showAddItem && (
              <div className="mb-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-3">New Line Item</p>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 sm:col-span-6">
                    <Input
                      label="Item Name"
                      placeholder="e.g. Labor, Part #123"
                      value={newItem?.name}
                      onChange={(e) => setNewItem(prev => ({ ...prev, name: e?.target?.value }))}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Input
                      label="Qty"
                      type="number"
                      min="1"
                      value={newItem?.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e?.target?.value }))}
                    />
                  </div>
                  <div className="col-span-8 sm:col-span-4">
                    <Input
                      label="Unit Price ($)"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newItem?.price}
                      onChange={(e) => setNewItem(prev => ({ ...prev, price: e?.target?.value }))}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => { setShowAddItem(false); setNewItem({ name: '', quantity: 1, price: '' }); }} className="flex-1">Cancel</Button>
                  <Button variant="default" size="sm" onClick={handleAddLineItem} disabled={!newItem?.name?.trim()} iconName="Plus" iconPosition="left" className="flex-1">Add Item</Button>
                </div>
              </div>
            )}

            {lineItems?.length === 0 && !showAddItem ? (
              <div className="text-center py-6 border border-dashed border-border rounded-lg">
                <Icon name="Package" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-40" />
                <p className="text-sm text-muted-foreground">No line items yet. Add products or services above.</p>
              </div>
            ) : lineItems?.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 px-3 py-1">
                  <span className="col-span-6 text-xs font-medium text-muted-foreground">Item</span>
                  <span className="col-span-2 text-xs font-medium text-muted-foreground text-center">Qty</span>
                  <span className="col-span-3 text-xs font-medium text-muted-foreground text-right">Price</span>
                  <span className="col-span-1" />
                </div>
                {lineItems?.map((item) => (
                  <div key={item?.id} className="border border-border rounded-lg">
                    <div className="grid grid-cols-12 gap-2 items-center px-3 py-2.5">
                      <div className="col-span-6">
                        <p className="text-sm font-medium text-foreground truncate">{item?.name}</p>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-sm text-muted-foreground">{item?.quantity}</span>
                      </div>
                      <div className="col-span-3 text-right">
                        <span className="text-sm font-medium text-foreground">${parseFloat(item?.price)?.toFixed(2)}</span>
                      </div>
                      <div className="col-span-1 flex items-center justify-end">
                        <button
                          onClick={() => handleRemoveLineItem(item?.id)}
                          className="p-1 rounded hover:bg-error/10 text-muted-foreground hover:text-error transition-colors"
                          title="Remove item"
                        >
                          <Icon name="Trash2" size={13} color="currentColor" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2 bg-muted/30 rounded-lg border border-border mt-2">
                  <span className="text-sm font-semibold text-foreground">Total</span>
                  <span className="text-sm font-bold text-foreground">${lineTotal?.toFixed(2)}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            variant="default"
            onClick={handleCreateTicket}
            loading={saving}
            disabled={!customerId}
            iconName="Plus"
            iconPosition="left"
            className="flex-1"
          >
            Create Ticket
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTicketModal;
